import { Link, createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import type { CurrencyCode } from '@kora/types';
import { formatMoney } from '../../lib/format.js';
import { platformLayoutRoute } from './platformLayout.js';
import {
  activateTenant,
  initializeTenantSubscription,
  cancelTenant,
  changeTenantTier,
  createInvoice,
  updateDraftInvoice,
  getMarkPaidPreview,
  getTenant,
  issueInvoice,
  listInvoices,
  markInvoicePaid,
  openInvoicePdf,
  restoreTenant,
  setTenantFounding,
  suspendTenant,
  voidInvoice,
  getEntitlements,
  overrideEntitlement,
  resetEntitlements,
  type InvoiceDetail,
  type SubscriptionDetail,
  type SubscriptionTier,
  type TenantDetail,
} from '../../api/platform.js';
import { ENTITLEMENT_KEYS, type EntitlementKey, type EntitlementValue } from '@kora/types';
import { PlatformApiError } from '../../lib/platform-auth.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { InvoiceStatusBadge } from '../../components/InvoiceStatusBadge.js';
import { PlatformReasonModal } from '../../components/platform/PlatformReasonModal.js';
import { PlatformConfirmTransitionModal } from '../../components/platform/PlatformConfirmTransitionModal.js';
import { ChangeTierModal } from '../../components/platform/ChangeTierModal.js';
import { SetFoundingModal } from '../../components/platform/SetFoundingModal.js';
import { ActivateModal } from '../../components/platform/ActivateModal.js';
import { InitializeSubscriptionModal } from '../../components/platform/InitializeSubscriptionModal.js';
import { DunningPanel } from '../../components/platform/DunningPanel.js';
import { GenerateInvoiceModal } from '../../components/platform/GenerateInvoiceModal.js';
import { MarkPaidModal } from '../../components/platform/MarkPaidModal.js';
import { VoidInvoiceModal } from '../../components/platform/VoidInvoiceModal.js';
import { OverrideEntitlementModal, isNumericKey } from '../../components/platform/OverrideEntitlementModal.js';

/**
 * /platform/tenants/:id — tenant detail + subscription panel + mutation
 * controls (Phase C-3). Consumes the C-2-amended detail response (which now
 * carries the `subscription` block) and the five C-2 mutation endpoints via
 * Indigo-Deep–skinned modals. Each successful mutation invalidates the detail
 * query so the panel reflects the new state.
 */
export const platformTenantDetailRoute = createRoute({
  getParentRoute: () => platformLayoutRoute,
  path: '/platform/tenants/$id',
  component: PlatformTenantDetailPage,
});

function PlatformTenantDetailPage(): JSX.Element {
  const { id } = platformTenantDetailRoute.useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['platform', 'tenant', id],
    queryFn: () => getTenant(id),
  });

  useEffect(() => {
    document.title = data ? `Platform — ${data.name}` : 'Platform — Tenant';
  }, [data]);

  if (isLoading) {
    return <Centered>Loading tenant…</Centered>;
  }

  if (isError) {
    const notFound = error instanceof PlatformApiError && error.status === 404;
    return (
      <Centered>
        <div role="alert" className="text-center space-y-3">
          <h2 className="text-lg font-medium text-gray-900">
            {notFound ? 'Tenant not found' : 'Could not load tenant'}
          </h2>
          {!notFound ? (
            <p className="text-sm text-gray-600">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          ) : null}
          <BackLink />
        </div>
      </Centered>
    );
  }

  if (!data) return <Centered>Tenant not found</Centered>;

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <div className="space-y-2">
        <BackLink />
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-900">{data.name}</h2>
          {data.status ? <StatusBadge status={data.status} /> : null}
        </div>
      </div>

      <Section title="Basic info">
        <Row label="Tenant ID" value={data.id} mono />
        <Row label="Name" value={data.name} />
        <Row label="Country" value={data.countryCode} />
        <Row label="Created" value={formatDateTime(data.createdAt)} />
        <Row label="Updated" value={formatDateTime(data.updatedAt)} />
      </Section>

      <SubscriptionPanel tenantId={data.id} tenantName={data.name} subscription={data.subscription} />

      <DunningPanel tenantId={data.id} tenantName={data.name} subscription={data.subscription} />

      <InvoicesPanel tenantId={data.id} tenantName={data.name} subscription={data.subscription} />

      <EntitlementsPanel tenantId={data.id} tenantName={data.name} subscription={data.subscription} />

      <BankProfileSection bankProfile={data.bankProfile} />

      <LoanPortfolioSection loanPortfolio={data.loanPortfolio} />

      <Section title="Headcount">
        <Row label="Total employees" value={String(data.employeeCount)} />
        <Row label="Active employees" value={String(data.activeEmployeeCount)} />
        <Row label="Seats used (cap-counted)" value={String(data.seatsUsed)} />
        <Row
          label="Cap"
          value={
            data.employeeCap === null
              ? 'Unlimited'
              : `${data.employeeCap}${data.capIsOverride ? ' (override)' : ''}`
          }
        />
        {data.employeeCap !== null ? (
          <Row label="Utilization" value={<UtilizationIndicator count={data.seatsUsed} cap={data.employeeCap} />} />
        ) : null}
        <Row label="Users" value={String(data.userCount)} />
      </Section>

      <Section title="Activity">
        <Row label="Payroll runs (lifetime)" value={String(data.payrollRunCount)} />
        <Row
          label="Last payroll run"
          value={data.lastPayrollRunDate ? formatDateTime(data.lastPayrollRunDate) : '—'}
        />
      </Section>

      <Section title="Compliance">
        <Row label="Configured" value={data.complianceConfig.configured ? 'Yes' : 'No'} />
        <Row
          label="Countries"
          value={data.complianceConfig.countries.length > 0 ? data.complianceConfig.countries.join(', ') : '—'}
        />
      </Section>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Subscription panel + mutation modals
// ---------------------------------------------------------------------------

function SubscriptionPanel({
  tenantId,
  tenantName,
  subscription,
}: {
  tenantId: string;
  tenantName: string;
  subscription: SubscriptionDetail | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<
    null | 'activate' | 'initialize' | 'suspend' | 'restore' | 'cancel' | 'cancel-detach' | 'tier' | 'founding'
  >(null);
  const [err, setErr] = useState('');

  const close = (): void => {
    setOpen(null);
    setErr('');
  };
  const onSuccess = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId] });
    close();
  };

  const activateMut = useMutation({
    mutationFn: (body: { reason: string; billingPeriodMonths: number; invoiceId?: string }) => activateTenant(tenantId, body),
    onSuccess,
  });
  const initializeMut = useMutation({
    mutationFn: (body: { periodStart: string; periodEnd: string; invoiceId?: string; reason: string }) =>
      initializeTenantSubscription(tenantId, body),
    onSuccess,
  });
  const suspendMut = useMutation({ mutationFn: (reason: string) => suspendTenant(tenantId, { reason }), onSuccess });
  const restoreMut = useMutation({ mutationFn: (reason: string) => restoreTenant(tenantId, { reason }), onSuccess });
  const cancelMut = useMutation({
    mutationFn: (body: { reason: string; detachProvider?: boolean }) => cancelTenant(tenantId, body),
    onSuccess,
  });
  const tierMut = useMutation({
    mutationFn: (body: { toTier: SubscriptionTier; reason: string }) => changeTenantTier(tenantId, body),
    onSuccess,
  });
  const foundingMut = useMutation({
    mutationFn: (body: { isFoundingCustomer: boolean; lockedRate?: string; lockEndsAt?: string; reason: string }) =>
      setTenantFounding(tenantId, body),
    onSuccess,
  });

  const errMsg = (e: unknown): string =>
    e instanceof PlatformApiError ? e.message : 'The action could not be completed.';

  /**
   * Did the normal cancel fail because PADDLE refused, on a Paddle-managed sub?
   * Only that failure opens the detach hatch — it is not a button an operator
   * can reach directly, so the real cancel is always attempted first.
   */
  const isPaddleCancelFailure = (e: unknown): boolean =>
    e instanceof PlatformApiError &&
    typeof e.body === 'object' &&
    e.body !== null &&
    (e.body as { error?: string }).error === 'PADDLE_CANCEL_FAILED' &&
    (e.body as { detachAvailable?: boolean }).detachAvailable === true;

  if (!subscription) {
    return (
      <Section title="Subscription">
        <Row label="Subscription" value="None (orphan org — no subscription row)" />
      </Section>
    );
  }

  const s = subscription;
  const terminal = s.status === 'cancelled';

  // Initialize affordance is status-aware (PR #154 follow-up): a `trial` sub
  // converts to its first paid period ("Start first paid period"); an `active`
  // sub with no period dates is a legacy "zombie" to remediate ("Initialize
  // subscription"). All other states (past_due/read_only/suspended/cancelled)
  // have dedicated paths (Dunning panel / Restore) and hide it — even when their
  // currentPeriod* happen to be NULL.
  const initMode: 'trial' | 'zombie' | null =
    s.status === 'trial'
      ? 'trial'
      : s.status === 'active' && !s.currentPeriodStart && !s.currentPeriodEnd
        ? 'zombie'
        : null;

  return (
    <div className="rounded border border-gray-200">
      <h3 className="px-4 py-2 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
        Subscription
      </h3>
      <dl className="divide-y divide-gray-100">
        <Row label="Tier" value={s.tier} />
        <Row label="Status" value={<StatusBadge status={s.status} size="sm" />} />
        {s.effectiveStatus !== s.status ? (
          <Row label="Effective status" value={<StatusBadge status={s.effectiveStatus} size="sm" />} />
        ) : null}
        {s.status === 'trial' ? (
          <>
            <Row label="Trial started" value={formatDateOrDash(s.trialStartedAt)} />
            <Row label="Trial ends" value={formatDateOrDash(s.trialEndsAt)} />
            <Row label="Days remaining" value={s.daysRemainingInTrial != null ? String(s.daysRemainingInTrial) : '—'} />
          </>
        ) : null}
        <Row label="Founding customer" value={s.isFoundingCustomer ? 'Yes' : 'No'} />
        {s.isFoundingCustomer ? (
          <>
            <Row label="Locked rate" value={s.lockedRate ?? '—'} />
            <Row label="Lock ends" value={formatDateOrDash(s.lockEndsAt)} />
          </>
        ) : null}
        <Row label="Payment provider" value={s.paymentProvider} />
        <Row label="Billing currency" value={s.billingCurrency} />
        {s.currentPeriodStart ? (
          <Row label="Current period start" value={formatDateTime(s.currentPeriodStart)} />
        ) : null}
        {s.currentPeriodEnd ? (
          <Row label="Current period end" value={formatDateTime(s.currentPeriodEnd)} />
        ) : null}
        {s.cancelledAt ? (
          <>
            <Row label="Cancelled at" value={formatDateTime(s.cancelledAt)} />
            <Row label="Cancellation reason" value={s.cancellationReason ?? '—'} />
          </>
        ) : null}
      </dl>

      <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-2">
        {terminal ? (
          <span className="text-sm text-gray-500">This subscription is cancelled (terminal). No actions available.</span>
        ) : (
          <>
            {initMode !== null ? (
              <button type="button" onClick={() => setOpen('initialize')} className={btnPrimary}>
                {initMode === 'trial' ? 'Start first paid period' : 'Initialize subscription'}
              </button>
            ) : null}
            {/* Activate is the restoration path for read_only/past_due. Trial →
                paid goes through "Start first paid period" above (which creates a
                SubscriptionPeriod row; Activate does not). */}
            {s.status === 'read_only' || s.status === 'past_due' ? (
              <button type="button" onClick={() => setOpen('activate')} className={btnPrimary}>
                Activate
              </button>
            ) : null}
            {s.status === 'suspended' ? (
              <button type="button" onClick={() => setOpen('restore')} className={btnSecondary}>
                Restore
              </button>
            ) : (
              <button type="button" onClick={() => setOpen('suspend')} className={btnSecondary}>
                Suspend
              </button>
            )}
            <button type="button" onClick={() => setOpen('cancel')} className={btnDanger}>
              Cancel
            </button>
            <button type="button" onClick={() => setOpen('tier')} className={btnSecondary}>
              Change tier
            </button>
            <button type="button" onClick={() => setOpen('founding')} className={btnSecondary}>
              {s.isFoundingCustomer ? 'Update Founding' : 'Set as Founding'}
            </button>
          </>
        )}
      </div>

      <PlatformReasonModal
        open={open === 'suspend'}
        onClose={close}
        title="Suspend tenant"
        description={`Suspending ${tenantName} sets the subscription to suspended — the tenant cannot make changes until restored.`}
        confirmLabel="Suspend"
        confirmVariant="danger"
        isSubmitting={suspendMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          try {
            await suspendMut.mutateAsync(reason);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <PlatformReasonModal
        open={open === 'restore'}
        onClose={close}
        title="Restore tenant"
        description={`Restoring ${tenantName} returns the subscription to active.`}
        confirmLabel="Restore"
        confirmVariant="primary"
        isSubmitting={restoreMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          try {
            await restoreMut.mutateAsync(reason);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <PlatformReasonModal
        open={open === 'cancel'}
        onClose={close}
        title="Cancel subscription"
        description={`Cancelling ${tenantName} is terminal — the subscription cannot be restored afterwards.`}
        confirmLabel="Cancel subscription"
        confirmVariant="danger"
        isSubmitting={cancelMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          try {
            await cancelMut.mutateAsync({ reason });
          } catch (e) {
            // The detach hatch is reachable ONLY from a real Paddle-cancel
            // failure: you cannot get to it without having tried the normal
            // cancel first and seen Paddle refuse. Anything else stays an error.
            if (isPaddleCancelFailure(e)) {
              setErr('');
              setOpen('cancel-detach');
              return;
            }
            setErr(errMsg(e));
          }
        }}
      />
      {/*
        Orphaned-ref escape hatch. Type-to-confirm (tenant legal name) + reason,
        reusing the D-5 confirm modal. The warning states the CONSEQUENCE rather
        than merely asking for a reason: the operator is asserting a fact about
        the world, and must do so knowingly. The server independently verifies
        the ref is not-found and refuses a live one (409 PROVIDER_REF_IS_LIVE),
        so this copy is the human half of a guard that is also structural.
      */}
      <PlatformConfirmTransitionModal
        open={open === 'cancel-detach'}
        onClose={close}
        title="Cancel without Paddle"
        warning={`Paddle would not cancel this subscription. This cancels ${tenantName} LOCALLY WITHOUT calling Paddle, and is only for a subscription Paddle can no longer act on (for example a provider reference that no longer exists). If Paddle is in fact still billing this subscription, that billing WILL CONTINUE and must be stopped in Paddle directly. The cancellation is recorded as provider-detached, with the reference kept in the audit trail.`}
        expectedConfirmText={tenantName}
        confirmLabel="Cancel without Paddle"
        isSubmitting={cancelMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          try {
            await cancelMut.mutateAsync({ reason, detachProvider: true });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <ChangeTierModal
        open={open === 'tier'}
        onClose={close}
        currentTier={s.tier}
        isSubmitting={tierMut.isPending}
        errorMessage={err}
        onConfirm={async (body) => {
          try {
            await tierMut.mutateAsync(body);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <SetFoundingModal
        open={open === 'founding'}
        onClose={close}
        currentIsFounding={s.isFoundingCustomer}
        isSubmitting={foundingMut.isPending}
        errorMessage={err}
        onConfirm={async (body) => {
          try {
            await foundingMut.mutateAsync(body);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <ActivateModal
        open={open === 'activate'}
        onClose={close}
        tenantId={tenantId}
        tenantName={tenantName}
        isSubmitting={activateMut.isPending}
        errorMessage={err}
        onConfirm={async (values) => {
          try {
            await activateMut.mutateAsync(values);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
      <InitializeSubscriptionModal
        open={open === 'initialize'}
        onClose={close}
        mode={initMode ?? 'zombie'}
        tenantId={tenantId}
        tenantName={tenantName}
        isSubmitting={initializeMut.isPending}
        errorMessage={err}
        onConfirm={async (values) => {
          try {
            await initializeMut.mutateAsync(values);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoices panel + invoice mutation modals (Phase D-2)
// ---------------------------------------------------------------------------

function InvoicesPanel({
  tenantId,
  tenantName,
  subscription,
}: {
  tenantId: string;
  tenantName: string;
  subscription: SubscriptionDetail | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  // tenantId IS the organization id — list every invoice for this tenant.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['platform', 'invoices', tenantId],
    queryFn: () => listInvoices({ organizationId: tenantId, limit: 200 }),
  });

  // Which modal is open, and the invoice it targets (for issue/mark-paid/void).
  const [modal, setModal] = useState<null | 'generate' | 'edit' | 'issue' | 'markPaid' | 'void'>(null);
  const [target, setTarget] = useState<InvoiceDetail | null>(null);
  const [err, setErr] = useState('');
  // ADR-0032: what the last mark-paid / void did that the operator must know about
  // (read_only not lifted, coverage not retracted, no service period…).
  const [notice, setNotice] = useState<string | null>(null);

  const close = (): void => {
    setModal(null);
    setTarget(null);
    setErr('');
  };
  const onSuccess = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'invoices', tenantId] });
    // Paying (or voiding) an invoice can change the SUBSCRIPTION too — refresh the
    // Subscription + Dunning panels, not only the invoice list.
    void queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId] });
    close();
  };

  // ADR-0032 D10: the consequence of marking paid, stated BEFORE confirming, from
  // the server's planner (the same one mark-paid runs). Fetched fresh each open.
  const preview = useQuery({
    queryKey: ['platform', 'invoices', tenantId, 'mark-paid-preview', target?.id],
    queryFn: () => getMarkPaidPreview(target!.id),
    enabled: modal === 'markPaid' && target != null,
    staleTime: 0,
    gcTime: 0,
  });
  const errMsg = (e: unknown): string =>
    e instanceof PlatformApiError ? e.message : 'The action could not be completed.';

  const createMut = useMutation({
    mutationFn: (body: Parameters<typeof createInvoice>[0]) => createInvoice(body),
    onSuccess,
  });
  const editMut = useMutation({
    mutationFn: (vars: { id: string; body: Parameters<typeof updateDraftInvoice>[1] }) => updateDraftInvoice(vars.id, vars.body),
    onSuccess,
  });
  const issueMut = useMutation({
    mutationFn: (vars: { id: string; reason: string }) => issueInvoice(vars.id, { reason: vars.reason }),
    onSuccess,
  });
  const markPaidMut = useMutation({
    mutationFn: (vars: { id: string; body: { reason: string; paymentReference: string; paidAt?: string } }) =>
      markInvoicePaid(vars.id, vars.body),
    onSuccess: (res) => {
      setNotice(res.coverage?.warning ? `${res.number}: ${res.coverage.warning}` : null);
      onSuccess();
    },
  });
  const voidMut = useMutation({
    mutationFn: (vars: { id: string; body: { reason: string; voidReason: string } }) =>
      voidInvoice(vars.id, vars.body),
    onSuccess: (res) => {
      setNotice(res.coverageWarning ? `${res.number}: ${res.coverageWarning}` : null);
      onSuccess();
    },
  });

  const invoices = data?.invoices ?? [];
  const canGenerate = subscription != null;

  return (
    <div className="rounded border border-gray-200">
      <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wide text-gray-500">Invoices</h3>
        <button
          type="button"
          onClick={() => {
            setErr('');
            setModal('generate');
          }}
          disabled={!canGenerate}
          className="rounded bg-indigo-primary px-3 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          title={canGenerate ? undefined : 'No subscription — cannot generate an invoice'}
        >
          Generate invoice
        </button>
      </div>

      {notice ? (
        <div className="px-4 py-2 border-b border-gray-100 bg-amber/10 text-sm text-gray-900 flex items-start justify-between gap-3" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-gray-500 hover:text-gray-900" aria-label="Dismiss notice">
            ✕
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="px-4 py-3 text-sm text-gray-500">Loading invoices…</p>
      ) : isError ? (
        <p className="px-4 py-3 text-sm text-red-600" role="alert">
          {error instanceof Error ? error.message : 'Could not load invoices.'}
        </p>
      ) : invoices.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500">No invoices yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="px-4 py-2 font-medium">Number</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Total</th>
              <th className="px-4 py-2 font-medium">Due</th>
              <th className="px-4 py-2 font-medium">Period</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td className="px-4 py-2 font-mono text-xs">{inv.number}</td>
                <td className="px-4 py-2">
                  <InvoiceStatusBadge status={inv.status} size="sm" />
                </td>
                <td className="px-4 py-2 text-right">
                  {formatMoney(inv.totalAmount, inv.currency as CurrencyCode)}
                </td>
                <td className="px-4 py-2">{formatDateOrDash(inv.dueDate)}</td>
                <td className="px-4 py-2 text-xs text-gray-600">
                  {inv.servicePeriodStart && inv.servicePeriodEnd
                    ? `${inv.servicePeriodStart.slice(0, 10)} to ${inv.servicePeriodEnd.slice(0, 10)}`
                    : '—'}
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => void openInvoicePdf(inv.id)}
                      className="text-indigo-deep hover:underline"
                    >
                      PDF
                    </button>
                    {inv.status === 'draft' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setTarget(inv);
                          setErr('');
                          setModal('edit');
                        }}
                        className="text-indigo-deep hover:underline"
                      >
                        Edit
                      </button>
                    ) : null}
                    {inv.status === 'draft' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setTarget(inv);
                          setErr('');
                          setModal('issue');
                        }}
                        className="text-indigo-deep hover:underline"
                      >
                        Issue
                      </button>
                    ) : null}
                    {inv.status === 'issued' || inv.status === 'overdue' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setTarget(inv);
                          setErr('');
                          setModal('markPaid');
                        }}
                        className="text-indigo-deep hover:underline"
                      >
                        Mark paid
                      </button>
                    ) : null}
                    {inv.status !== 'voided' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setTarget(inv);
                          setErr('');
                          setModal('void');
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Void
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {subscription ? (
        <GenerateInvoiceModal
          open={modal === 'generate'}
          onClose={close}
          tenantName={tenantName}
          currency={subscription.billingCurrency}
          renewFrom={subscription.currentPeriodEnd}
          isSubmitting={createMut.isPending}
          errorMessage={err}
          onConfirm={async (body) => {
            try {
              await createMut.mutateAsync({ subscriptionId: subscription.id, ...body });
            } catch (e) {
              setErr(errMsg(e));
            }
          }}
        />
      ) : null}

      {/* Edit a DRAFT (2026-09-13). Keyed per invoice: the form reads its defaults
          once, at mount. Mounted only while editing, so reopening re-reads the
          saved draft rather than a stale form. */}
      {subscription && modal === 'edit' && target ? (
        <GenerateInvoiceModal
          key={target.id}
          open
          onClose={close}
          tenantName={tenantName}
          currency={target.currency}
          initial={target}
          renewFrom={subscription.currentPeriodEnd}
          isSubmitting={editMut.isPending}
          errorMessage={err}
          onConfirm={async ({ currency: _currency, ...body }) => {
            try {
              await editMut.mutateAsync({ id: target.id, body });
            } catch (e) {
              setErr(errMsg(e));
            }
          }}
        />
      ) : null}

      <MarkPaidModal
        open={modal === 'markPaid'}
        onClose={close}
        invoiceNumber={target?.number ?? ''}
        consequence={preview.isSuccess ? preview.data.coverage : undefined}
        consequenceError={preview.isError}
        isSubmitting={markPaidMut.isPending}
        errorMessage={err}
        onConfirm={async (body) => {
          if (!target) return;
          try {
            await markPaidMut.mutateAsync({ id: target.id, body });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />

      <VoidInvoiceModal
        open={modal === 'void'}
        onClose={close}
        invoiceNumber={target?.number ?? ''}
        isPaid={target?.status === 'paid'}
        isSubmitting={voidMut.isPending}
        errorMessage={err}
        onConfirm={async (body) => {
          if (!target) return;
          try {
            await voidMut.mutateAsync({ id: target.id, body });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />

      <PlatformReasonModal
        open={modal === 'issue'}
        onClose={close}
        title="Issue invoice"
        description={issueInvoiceDescription(target)}
        confirmLabel="Issue"
        confirmVariant="primary"
        isSubmitting={issueMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          if (!target) return;
          try {
            await issueMut.mutateAsync({ id: target.id, reason });
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
    </div>
  );
}

/**
 * What the operator is told before issuing (ADR-0032 D10 follow-up, 2026-09-17).
 *
 * An invoice that states NO service period is a one-off charge: paying it renews
 * nothing (`coverage.result = 'no_service_period'`). That is only surfaced today at
 * MARK PAID — by which point the invoice is issued, **can no longer be edited**, and
 * the customer already holds the PDF. Two production invoices took that shape before
 * anyone noticed: MUL-2026-0012 was paid and moved nothing, and MUL-2026-0011 sat in
 * MokoAfrika's "Awaiting payment" looking exactly like a renewal.
 *
 * Issue is the last moment the mistake is free, because a draft can still be edited.
 */
export function issueInvoiceDescription(invoice: Pick<InvoiceDetail, 'number' | 'servicePeriodStart' | 'servicePeriodEnd'> | null): string {
  const number = invoice?.number ?? '';
  const base = `Issue invoice ${number} — it becomes payable and can no longer be edited.`;
  if (invoice && invoice.servicePeriodStart && invoice.servicePeriodEnd) {
    return `${base} Paying it will extend coverage to ${invoice.servicePeriodEnd.slice(0, 10)}.`;
  }
  return `${base} ⚠️ This invoice states no service period, so paying it will NOT renew the subscription. Edit the draft to add one, or continue if it is a one-off charge (a setup fee or a credit).`;
}

// ---------------------------------------------------------------------------
// Entitlements panel + override / reset modals (Phase D-3)
// ---------------------------------------------------------------------------

// Catalog display order: boolean keys (alphabetical) first, then numeric keys
// (alphabetical) — per the locked UI decision.
const SORTED_ENTITLEMENT_KEYS: EntitlementKey[] = [...ENTITLEMENT_KEYS].sort((a, b) => {
  const an = isNumericKey(a) ? 1 : 0;
  const bn = isNumericKey(b) ? 1 : 0;
  return an !== bn ? an - bn : a.localeCompare(b);
});

function fmtEntitlement(v: EntitlementValue): string {
  if (v === null) return 'Unlimited';
  if (typeof v === 'boolean') return v ? 'On' : 'Off';
  return String(v);
}

function EntitlementsPanel({
  tenantId,
  tenantName,
  subscription,
}: {
  tenantId: string;
  tenantName: string;
  subscription: SubscriptionDetail | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['platform', 'entitlements', tenantId],
    queryFn: () => getEntitlements(tenantId),
    enabled: subscription != null, // no subscription → no entitlement row
  });

  const [modal, setModal] = useState<null | 'override' | 'reset'>(null);
  const [editKey, setEditKey] = useState<EntitlementKey | undefined>(undefined);
  const [err, setErr] = useState('');

  const close = (): void => {
    setModal(null);
    setEditKey(undefined);
    setErr('');
  };
  const onSuccess = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'entitlements', tenantId] });
    close();
  };
  const errMsg = (e: unknown): string =>
    e instanceof PlatformApiError ? e.message : 'The action could not be completed.';

  const overrideMut = useMutation({
    mutationFn: (body: { key: EntitlementKey; value: EntitlementValue; reason: string }) =>
      overrideEntitlement(tenantId, body),
    onSuccess,
  });
  const resetMut = useMutation({
    mutationFn: (reason: string) => resetEntitlements(tenantId, reason),
    onSuccess,
  });

  // Defensive: only treat the query result as a usable view when it has the
  // full EntitlementView shape (the server always returns it; this guards
  // against a partial/unexpected body so the panel never crashes).
  const view =
    data && data.overrides && data.tierDefaults && data.effectiveEntitlements ? data : null;

  return (
    <div className="rounded border border-gray-200">
      <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wide text-gray-500">Entitlements</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setEditKey(undefined);
              setErr('');
              setModal('override');
            }}
            disabled={!view}
            className="rounded bg-indigo-primary px-3 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Override
          </button>
          <button
            type="button"
            onClick={() => {
              setErr('');
              setModal('reset');
            }}
            disabled={!view}
            className="rounded border border-indigo-deep px-3 py-1 text-xs text-indigo-deep hover:bg-indigo-deep hover:text-white disabled:opacity-50"
          >
            Reset all
          </button>
        </div>
      </div>

      {subscription == null ? (
        <p className="px-4 py-3 text-sm text-gray-500">No subscription — no entitlements.</p>
      ) : isLoading ? (
        <p className="px-4 py-3 text-sm text-gray-500">Loading entitlements…</p>
      ) : isError ? (
        <p className="px-4 py-3 text-sm text-red-600" role="alert">
          {error instanceof Error ? error.message : 'Could not load entitlements.'}
        </p>
      ) : view ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="px-4 py-2 font-medium">Key</th>
              <th className="px-4 py-2 font-medium">Default</th>
              <th className="px-4 py-2 font-medium">Effective</th>
              <th className="px-4 py-2 font-medium">Source</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {SORTED_ENTITLEMENT_KEYS.map((key) => {
              const isOverride = Object.prototype.hasOwnProperty.call(view.overrides, key);
              return (
                <tr key={key}>
                  <td className="px-4 py-2 font-mono text-xs">{key}</td>
                  <td className="px-4 py-2">{fmtEntitlement(view.tierDefaults[key])}</td>
                  <td className="px-4 py-2 font-medium">{fmtEntitlement(view.effectiveEntitlements[key])}</td>
                  <td className="px-4 py-2">
                    {isOverride ? (
                      <span className="text-indigo">override</span>
                    ) : (
                      <span className="text-gray-500">tier default</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        setEditKey(key);
                        setErr('');
                        setModal('override');
                      }}
                      className="text-indigo-deep hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {view ? (
        <OverrideEntitlementModal
          open={modal === 'override'}
          onClose={close}
          tenantName={tenantName}
          view={view}
          initialKey={editKey}
          isSubmitting={overrideMut.isPending}
          errorMessage={err}
          onConfirm={async (body) => {
            try {
              await overrideMut.mutateAsync(body);
            } catch (e) {
              setErr(errMsg(e));
            }
          }}
        />
      ) : null}

      <PlatformReasonModal
        open={modal === 'reset'}
        onClose={close}
        title="Reset entitlements"
        description={`Reset all entitlement overrides for ${tenantName}? Every entitlement reverts to its tier default; any custom overrides currently in effect are cleared.`}
        confirmLabel="Reset all"
        confirmVariant="danger"
        isSubmitting={resetMut.isPending}
        errorMessage={err}
        onConfirm={async (reason) => {
          try {
            await resetMut.mutateAsync(reason);
          } catch (e) {
            setErr(errMsg(e));
          }
        }}
      />
    </div>
  );
}

const btnSecondary =
  'rounded border border-indigo-deep px-3 py-1.5 text-sm text-indigo-deep hover:bg-indigo-deep hover:text-white';
const btnDanger = 'rounded border border-red-600 px-3 py-1.5 text-sm text-red-600 hover:bg-red-600 hover:text-white';
// Primary-emphasis (filled Indigo Primary) — the revenue action leads the row.
const btnPrimary = 'rounded bg-indigo-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90';

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

function BackLink(): JSX.Element {
  return (
    <Link to="/platform/tenants" className="text-sm text-indigo-deep hover:underline">
      ← Back to tenants
    </Link>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="rounded border border-gray-200">
      <h3 className="px-4 py-2 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">{title}</h3>
      <dl className="divide-y divide-gray-100">{children}</dl>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between px-4 py-2 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * Cap utilization read-out (Phase D-4): percentage + count/cap with a traffic-
 * light colour — green <70%, amber 70–90%, red ≥90% (the warning threshold).
 * gray/red are default Tailwind scales; amber is a single-token (use bare).
 */
// ---------------------------------------------------------------------------
// Bank profile (read-only; bank-export feature)
// ---------------------------------------------------------------------------

function BankProfileSection({
  bankProfile,
}: {
  bankProfile: TenantDetail['bankProfile'];
}): JSX.Element {
  if (!bankProfile) {
    return (
      <Section title="Bank profile">
        <Row label="Status" value="Not configured" />
      </Section>
    );
  }
  if (bankProfile.accounts.length === 0) {
    return (
      <Section title="Bank profile">
        <Row label="Accounts" value="No accounts declared" />
        {bankProfile.narrationTemplate ? (
          <Row label="Custom narration" value={bankProfile.narrationTemplate} />
        ) : null}
      </Section>
    );
  }
  return (
    <div className="rounded border border-gray-200">
      <h3 className="px-4 py-2 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">Bank profile</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
            <th className="px-4 py-2 font-medium">Bank</th>
            <th className="px-4 py-2 font-medium">Account number</th>
            <th className="px-4 py-2 font-medium">Account name</th>
            <th className="px-4 py-2 font-medium">Currency</th>
            <th className="px-4 py-2 font-medium">Purpose</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {bankProfile.accounts.map((a, i) => (
            <tr key={i}>
              <td className="px-4 py-2">{a.bankName}</td>
              <td className="px-4 py-2 font-mono text-xs">{a.accountNumber}</td>
              <td className="px-4 py-2">{a.accountName}</td>
              <td className="px-4 py-2">{a.currency}</td>
              <td className="px-4 py-2 text-gray-500">{a.purpose ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {bankProfile.narrationTemplate ? (
        <div className="px-4 py-2 border-t border-gray-100 text-sm text-gray-600">
          Custom narration: {bankProfile.narrationTemplate}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loan portfolio composition (read-only; loan-migration feature)
// ---------------------------------------------------------------------------

function LoanPortfolioSection({
  loanPortfolio,
}: {
  loanPortfolio: TenantDetail['loanPortfolio'];
}): JSX.Element {
  const { activeLoanCount, originationBreakdown, totalOutstandingBalance } = loanPortfolio;
  if (activeLoanCount === 0) {
    return (
      <Section title="Loan portfolio">
        <Row label="Active loans" value="No active loans" />
      </Section>
    );
  }
  const outstanding = Object.entries(totalOutstandingBalance);
  return (
    <Section title="Loan portfolio">
      <Row label="Active loans (live)" value={String(activeLoanCount)} />
      <Row label="Employee-requested" value={String(originationBreakdown.in_app_request)} />
      <Row label="HR-initiated" value={String(originationBreakdown.hr_direct_create)} />
      <Row label="Migrated" value={String(originationBreakdown.migration)} />
      <Row
        label="Outstanding balance"
        value={outstanding.length ? outstanding.map(([cur, amt]) => `${cur} ${amt}`).join(' · ') : '—'}
      />
    </Section>
  );
}

function UtilizationIndicator({ count, cap }: { count: number; cap: number }): JSX.Element {
  const pct = cap > 0 ? Math.round((count / cap) * 100) : 0;
  const cls = pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-amber' : 'text-green-600';
  return (
    <span className={`font-medium ${cls}`} aria-label={`Utilization ${pct}%`}>
      {pct}% ({count}/{cap})
    </span>
  );
}

function Centered({ children }: { children: ReactNode }): JSX.Element {
  return <div className="p-8 text-center text-gray-500">{children}</div>;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().replace('T', ' ').slice(0, 16);
}

function formatDateOrDash(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}
